import { type PointerEvent as ReactPointerEvent, useEffect, useRef } from 'react';

/** How long a finger rests on an element before it is a long press. */
const DELAY = 450;
/** How far it may move meanwhile (more is a scroll). */
const TOLERANCE = 10;

/**
 * Swallows the click a finger lifted after a long press makes: it lands on whatever the long
 * press opened under the finger (a sheet's row), not on the element pressed. The next touch
 * stops it, so a click that never comes swallows nothing more. Returns how to stop it now.
 */
function swallowReleaseClick(): () => void {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    stop();
  };
  const stop = () => {
    window.removeEventListener('click', swallow, true);
    window.removeEventListener('pointerdown', stop, true);
  };
  window.addEventListener('click', swallow, true);
  window.addEventListener('pointerdown', stop, true);
  return stop;
}

/**
 * A long press (or a right click): `onLongPress` runs, and the click the release would make is
 * swallowed, so a row opens on a tap and offers its actions on a long press.
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const release = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      release.current?.();
    },
    [],
  );
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  };
  return {
    onPointerDown(event: ReactPointerEvent) {
      if (event.pointerType === 'mouse') return;
      fired.current = false;
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        timer.current = null;
        release.current = swallowReleaseClick();
        navigator.vibrate?.(10);
        onLongPress();
      }, DELAY);
    },
    onPointerMove(event: ReactPointerEvent) {
      const start = origin.current;
      if (start === null) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > TOLERANCE) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu(event: { preventDefault(): void }) {
      event.preventDefault();
      cancel();
      if (fired.current) return;
      fired.current = true;
      onLongPress();
    },
    onClickCapture(event: { preventDefault(): void; stopPropagation(): void }) {
      if (!fired.current) return;
      fired.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
